/** Ham pointer; sahne / Engine / Orbit ile bağlantı yok. */
export const POINTER_GESTURE_PRESS = "pointer-gesture-press";
export const POINTER_GESTURE_RELEASE = "pointer-gesture-release";
export const POINTER_GESTURE_MOVE = "pointer-gesture-move";
export const POINTER_GESTURE_PAN_BEGIN = "pointer-gesture-pan-begin";
export const POINTER_GESTURE_PAN_END = "pointer-gesture-pan-end";
export const POINTER_GESTURE_PITCH_BEGIN = "pointer-gesture-pitch-begin";
export const POINTER_GESTURE_PITCH_END = "pointer-gesture-pitch-end";
export const POINTER_GESTURE_PINCH_BEGIN = "pointer-gesture-pinch-begin";
export const POINTER_GESTURE_PINCH_END = "pointer-gesture-pinch-end";

export type PointerGestureModifiers = {
  ctrl: boolean;
  meta: boolean;
  shift: boolean;
};

export type PointerGesturePressDetail = {
  clientX: number;
  clientY: number;
  pointerId: number;
  pointerType: string;
  button: number;
  isPrimary: boolean;
  modifiers: PointerGestureModifiers;
  activePointerCount: number;
  /** Tüketiciler (ör. orbit çatışması) için orijinal pointer olayı. */
  sourceEvent?: PointerEvent;
};

export type PointerGestureReleaseDetail = PointerGesturePressDetail & {
  isDoubleClick: boolean;
};

export type PointerGestureMoveDetail = {
  clientX: number;
  clientY: number;
  pointerId: number;
  pointerType: string;
  button: number;
  isPrimary: boolean;
  modifiers: PointerGestureModifiers;
  activePointerCount: number;
  deltaX: number;
  deltaY: number;
  /** `touchCount >= 2` veya pinch oturumu sırasında iki uç arası mesafe (px). */
  pinchSpan?: number;
  sourceEvent?: PointerEvent;
};

export type PointerGestureAxisSessionDetail = {
  pointerId: number;
  pointerType: string;
  originX: number;
  originY: number;
  clientX: number;
  clientY: number;
  totalDeltaX: number;
  totalDeltaY: number;
  activePointerCount: number;
};

export type PointerGesturePinchDetail = {
  activePointerCount: number;
  /** İki uç nokta arası ekran mesafesi (px). */
  span: number;
};

export interface PointerGestureInterpreterOptions {
  target: HTMLElement;
  /** Optional viewport-scoped destination; defaults to a private EventTarget. */
  eventTarget?: EventTarget;
  /**
   * `host`: olaylar `target` üzerinde (bubble). `internal`: yalnızca `events` EventTarget.
   */
  emitTo?: "host" | "internal";
  /** Pan/pitch oturumu için minimum toplam hareket (px). */
  moveThresholdPx?: number;
  /**
   * `true` ise dinleyiciler capture fazında; canvas’ta aynı fazda daha sonra eklenen
   * dinleyicilerden önce çalışır (DesignerTransformer sırası için `true` kullanın).
   */
  useCapture?: boolean;
}

type PtrEntry = { clientX: number; clientY: number; pointerType: string };
type ClickEntry = {
  clientX: number;
  clientY: number;
  pointerType: string;
  button: number;
  time: number;
};

/**
 * Canvas / viewport ham pointer akışını dinler; ekran düzleminde pan / pitch / pinch
 * oturumlarını CustomEvent olarak yayınlar. preventDefault / stopPropagation kullanılmaz.
 */
export class PointerGestureInterpreter {
  readonly events: EventTarget;

  private readonly _target: HTMLElement;
  private readonly _emitTo: "host" | "internal";
  private readonly _capture: boolean;
  private readonly _threshold: number;
  private readonly _lastPos = new Map<number, PtrEntry>();
  private readonly _active = new Map<number, PtrEntry>();
  private readonly _pressStart = new Map<number, ClickEntry>();
  private _lastClick?: ClickEntry;

  private _disposed = false;
  private _pinchActive = false;
  /** 'none' | 'pan' | 'pitch' — tek işaretçi eksen oturumu */
  private _axisKind: "none" | "pan" | "pitch" = "none";
  private _classified = false;
  private _originX = 0;
  private _originY = 0;
  private _axisPointerId: number | null = null;
  private _axisPointerType = "";

  private readonly _onPointerDown: (e: PointerEvent) => void;
  private readonly _onPointerMove: (e: PointerEvent) => void;
  private readonly _onPointerUp: (e: PointerEvent) => void;
  private readonly _onPointerCancel: (e: PointerEvent) => void;

  constructor(options: PointerGestureInterpreterOptions) {
    this._target = options.target;
    this._emitTo = options.emitTo ?? "internal";
    this._capture = options.useCapture ?? false;
    this._threshold = Math.max(1, options.moveThresholdPx ?? 8);
    this.events = options.eventTarget ?? new EventTarget();

    this._onPointerDown = this._handlePointerDown.bind(this);
    this._onPointerMove = this._handlePointerMove.bind(this);
    this._onPointerUp = this._handlePointerUp.bind(this);
    this._onPointerCancel = this._handlePointerUp.bind(this);

    const cap = this._capture;
    this._target.addEventListener("pointerdown", this._onPointerDown, cap);
    this._target.addEventListener("pointermove", this._onPointerMove, cap);
    this._target.addEventListener("pointerup", this._onPointerUp, cap);
    this._target.addEventListener("pointercancel", this._onPointerCancel, cap);
    this._target.addEventListener("lostpointercapture", this._onPointerUp, cap);
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    const cap = this._capture;
    this._target.removeEventListener("pointerdown", this._onPointerDown, cap);
    this._target.removeEventListener("pointermove", this._onPointerMove, cap);
    this._target.removeEventListener("pointerup", this._onPointerUp, cap);
    this._target.removeEventListener("pointercancel", this._onPointerCancel, cap);
    this._target.removeEventListener("lostpointercapture", this._onPointerUp, cap);
    this._active.clear();
    this._lastPos.clear();
    this._pressStart.clear();
    this._lastClick = undefined;
    this._pinchActive = false;
    this._axisKind = "none";
    this._classified = false;
    this._axisPointerId = null;
    this._axisPointerType = "";
  }

  private _emit(type: string, detail: unknown): void {
    const ev = new CustomEvent(type, {
      detail,
      bubbles: this._emitTo === "host",
      composed: this._emitTo === "host",
    });
    if (this._emitTo === "host") {
      this._target.dispatchEvent(ev);
    } else {
      this.events.dispatchEvent(ev);
    }
  }

  private _modifierDetail(e: PointerEvent): PointerGestureModifiers {
    return {
      ctrl: e.ctrlKey,
      meta: e.metaKey,
      shift: e.shiftKey,
    };
  }

  private _pressDetail(e: PointerEvent): PointerGesturePressDetail {
    return {
      clientX: e.clientX,
      clientY: e.clientY,
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      button: e.button,
      isPrimary: e.isPrimary,
      modifiers: this._modifierDetail(e),
      activePointerCount: this._active.size,
      sourceEvent: e,
    };
  }

  private _releaseDetail(
    e: PointerEvent,
    remainingPointerCount: number,
    isDoubleClick: boolean,
  ): PointerGestureReleaseDetail {
    return {
      clientX: e.clientX,
      clientY: e.clientY,
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      button: e.button,
      isPrimary: e.isPrimary,
      modifiers: this._modifierDetail(e),
      activePointerCount: remainingPointerCount,
      sourceEvent: e,
      isDoubleClick,
    };
  }

  private _isDoubleClickRelease(
    e: PointerEvent,
    remainingPointerCount: number,
  ): boolean {
    const press = this._pressStart.get(e.pointerId);
    if (!press) return false;
    if (remainingPointerCount !== 0 || this._pinchActive) return false;

    const releaseDistance = Math.hypot(
      e.clientX - press.clientX,
      e.clientY - press.clientY,
    );
    if (releaseDistance > this._threshold) {
      this._lastClick = undefined;
      return false;
    }

    const now = performance.now();
    const previous = this._lastClick;
    const isDouble =
      previous?.pointerType === e.pointerType &&
      previous.button === e.button &&
      now - previous.time <= 350 &&
      Math.hypot(
        e.clientX - previous.clientX,
        e.clientY - previous.clientY,
      ) <= 8;

    this._lastClick = isDouble
      ? undefined
      : {
          clientX: e.clientX,
          clientY: e.clientY,
          pointerType: e.pointerType,
          button: e.button,
          time: now,
        };

    return isDouble;
  }

  private _endAxisSessionIfNeeded(clientX: number, clientY: number): void {
    if (this._axisKind === "none" || this._axisPointerId == null) return;
    const d: PointerGestureAxisSessionDetail = {
      pointerId: this._axisPointerId,
      pointerType: this._axisPointerType,
      originX: this._originX,
      originY: this._originY,
      clientX,
      clientY,
      totalDeltaX: clientX - this._originX,
      totalDeltaY: clientY - this._originY,
      activePointerCount: this._active.size,
    };
    if (this._axisKind === "pan") {
      this._emit(POINTER_GESTURE_PAN_END, d);
    } else {
      this._emit(POINTER_GESTURE_PITCH_END, d);
    }
    this._axisKind = "none";
    this._classified = false;
    this._axisPointerId = null;
    this._axisPointerType = "";
  }

  private _maybeBeginPinch(): void {
    if (this._active.size < 2 || this._pinchActive) return;
    this._axisPointerId = null;
    this._axisPointerType = "";
    this._classified = false;
    this._axisKind = "none";
    this._pinchActive = true;
    this._emit(POINTER_GESTURE_PINCH_BEGIN, this._pinchDetail());
  }

  private _pinchDetail(): PointerGesturePinchDetail {
    return {
      activePointerCount: this._active.size,
      span: this._twoPointerSpan(),
    };
  }

  private _twoPointerSpan(): number {
    if (this._active.size < 2) return 0;
    const pts = [...this._active.values()];
    const a = pts[0]!;
    const b = pts[1]!;
    const dx = b.clientX - a.clientX;
    const dy = b.clientY - a.clientY;
    return Math.hypot(dx, dy);
  }

  private _handlePointerDown(e: PointerEvent): void {
    if (this._disposed) return;

    this._active.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
      pointerType: e.pointerType,
    });
    this._pressStart.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
      pointerType: e.pointerType,
      button: e.button,
      time: performance.now(),
    });
    this._lastPos.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
      pointerType: e.pointerType,
    });

    this._emit(POINTER_GESTURE_PRESS, this._pressDetail(e));

    if (this._active.size >= 2) {
      if (this._axisKind !== "none" && this._axisPointerId != null) {
        const p = this._active.get(this._axisPointerId);
        this._endAxisSessionIfNeeded(
          p?.clientX ?? e.clientX,
          p?.clientY ?? e.clientY,
        );
      }
      this._maybeBeginPinch();
    } else if (this._active.size === 1) {
      this._originX = e.clientX;
      this._originY = e.clientY;
      this._axisPointerId = e.pointerId;
      this._axisPointerType = e.pointerType;
      this._classified = false;
      this._axisKind = "none";
    }
  }

  private _handlePointerMove(e: PointerEvent): void {
    if (this._disposed) return;

    const prev = this._lastPos.get(e.pointerId);
    this._lastPos.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
      pointerType: e.pointerType,
    });
    if (this._active.has(e.pointerId)) {
      this._active.set(e.pointerId, {
        clientX: e.clientX,
        clientY: e.clientY,
        pointerType: e.pointerType,
      });
    }

    const deltaX = prev ? e.clientX - prev.clientX : 0;
    const deltaY = prev ? e.clientY - prev.clientY : 0;

    const moveDetail: PointerGestureMoveDetail = {
      clientX: e.clientX,
      clientY: e.clientY,
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      button: e.button,
      isPrimary: e.isPrimary,
      modifiers: this._modifierDetail(e),
      activePointerCount: this._active.size,
      deltaX,
      deltaY,
      sourceEvent: e,
      ...(this._pinchActive && this._active.size >= 2
        ? { pinchSpan: this._twoPointerSpan() }
        : {}),
    };
    this._emit(POINTER_GESTURE_MOVE, moveDetail);

    if (this._pinchActive || this._active.size !== 1) return;
    if (this._axisPointerId !== e.pointerId) return;
    if (this._classified) return;

    const tdx = e.clientX - this._originX;
    const tdy = e.clientY - this._originY;
    const dist = Math.hypot(tdx, tdy);
    if (dist < this._threshold) return;

    this._classified = true;
    if (Math.abs(tdx) > Math.abs(tdy)) {
      this._axisKind = "pan";
      this._emit(POINTER_GESTURE_PAN_BEGIN, {
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        originX: this._originX,
        originY: this._originY,
        clientX: e.clientX,
        clientY: e.clientY,
        totalDeltaX: tdx,
        totalDeltaY: tdy,
        activePointerCount: 1,
      } satisfies PointerGestureAxisSessionDetail);
    } else {
      this._axisKind = "pitch";
      this._emit(POINTER_GESTURE_PITCH_BEGIN, {
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        originX: this._originX,
        originY: this._originY,
        clientX: e.clientX,
        clientY: e.clientY,
        totalDeltaX: tdx,
        totalDeltaY: tdy,
        activePointerCount: 1,
      } satisfies PointerGestureAxisSessionDetail);
    }
  }

  private _handlePointerUp(e: PointerEvent): void {
    if (this._disposed) return;

    if (!this._active.has(e.pointerId)) {
      this._lastPos.delete(e.pointerId);
      this._pressStart.delete(e.pointerId);
      return;
    }

    const remaining = this._active.size - 1;
    const isDoubleClick = this._isDoubleClickRelease(e, remaining);
    this._emit(
      POINTER_GESTURE_RELEASE,
      this._releaseDetail(e, remaining, isDoubleClick),
    );

    const wasPinch = this._pinchActive;
    const wasAxisPointer = this._axisPointerId === e.pointerId;

    if (wasAxisPointer && this._axisKind !== "none") {
      this._endAxisSessionIfNeeded(e.clientX, e.clientY);
    }

    this._active.delete(e.pointerId);
    this._lastPos.delete(e.pointerId);
    this._pressStart.delete(e.pointerId);

    if (wasPinch && this._active.size < 2) {
      this._pinchActive = false;
      this._emit(POINTER_GESTURE_PINCH_END, this._pinchDetail());
      if (this._active.size === 1) {
        const [pid, p] = this._active.entries().next().value!;
        this._originX = p.clientX;
        this._originY = p.clientY;
        this._axisPointerId = pid;
        this._axisPointerType = p.pointerType;
        this._classified = false;
        this._axisKind = "none";
      }
    }

    if (wasAxisPointer && this._axisKind === "none") {
      this._axisPointerId = null;
      this._classified = false;
      this._axisPointerType = "";
    }

    if (this._active.size === 0) {
      this._pinchActive = false;
      this._axisKind = "none";
      this._classified = false;
      this._axisPointerId = null;
      this._axisPointerType = "";
    }
  }
}
